import { ReactElement, ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { colors } from '~/styles/colors';
import {
  ErrorText, FieldLabel, HelperText, mobileQuery,
} from '../ds';
import { parseFormNumber } from '../helpers';
import { DamEcoSimplifiedFormValues, DamEcoSimplifiedValidationKey } from '../types';
import { InfoHint } from './controls';
import {
  ChevronDownIcon, ChevronUpIcon, MinusIcon, PlusIcon,
} from './icons';

export type StepperFieldName = 'setpoint' | 'upperHysteresis' | 'lowerHysteresis' | 'lti' | 'stageAddDelay' | 'stageReduceDelay' | 'ventStart' | 'ventEnd';

const STROKE = colors.stroke['soft-200'];

const Box = styled.div<{ $invalid: boolean; $disabled: boolean }>`
  display: grid;
  grid-template-areas: "value inc" "value dec";
  grid-template-columns: minmax(0, 1fr) 40px;
  height: 48px;
  overflow: hidden;
  border: 1px solid ${({ $invalid }) => ($invalid ? colors.celsius['danger-base'] : STROKE)};
  border-radius: 8px;
  background: ${({ $disabled }) => ($disabled ? colors.LightGrey : colors.bg['white-0'])};

  ${mobileQuery} {
    grid-template-areas: "dec value inc";
    grid-template-columns: 52px minmax(0, 1fr) 52px;
    height: 52px;
  }
`;

const ValueArea = styled.label`
  grid-area: value;
  display: flex;
  align-items: baseline;
  align-self: center;
  gap: 6px;
  min-width: 0;
  padding: 0 16px;

  ${mobileQuery} {
    justify-content: center;
  }
`;

const ValueInput = styled.input`
  flex: none;
  min-width: 1ch;
  max-width: 100%;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: ${colors.text['main-900']};

  ${mobileQuery} {
    text-align: right;
  }
`;

const Unit = styled.span`
  font-size: 12px;
  color: ${colors.text['sub-350']};
`;

const StepButton = styled.button<{ $area: 'inc' | 'dec' }>`
  grid-area: ${({ $area }) => $area};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-left: 1px solid ${STROKE};
  background: ${colors.bg['white-0']};
  color: ${colors.text['sub-500']};
  cursor: pointer;

  ${({ $area }) => $area === 'inc' && css`border-bottom: 1px solid ${STROKE};`}

  &:hover:not(:disabled) {
    background: ${colors.LightGrey};
    color: ${colors.brand.primary.base};
  }

  &:disabled {
    cursor: not-allowed;
    background: ${colors.LightGrey};
  }

  & > .mobile-icon {
    display: none;
  }

  ${mobileQuery} {
    border-bottom: none;
    border-left: ${({ $area }) => ($area === 'inc' ? `1px solid ${STROKE}` : 'none')};
    border-right: ${({ $area }) => ($area === 'dec' ? `1px solid ${STROKE}` : 'none')};

    & > .desktop-icon {
      display: none;
    }

    & > .mobile-icon {
      display: inline;
    }
  }
`;

const Label = styled(FieldLabel)<{ $alignRows?: boolean }>`
  ${({ $alignRows }) => $alignRows && css`
    align-items: flex-start;
    min-height: 32px;

    ${mobileQuery} {
      min-height: 0;
    }
  `}
`;

const Helper = styled(HelperText)<{ $alignRows?: boolean }>`
  ${({ $alignRows }) => $alignRows && css`
    min-height: 52px;

    ${mobileQuery} {
      min-height: 0;
    }
  `}
`;

interface StepperFieldProps {
  name: StepperFieldName;
  label: string;
  unit: string;
  /** Faixa aplicada só aos botões; o que vale para salvar é a validação. */
  limits?: { min?: number; max?: number; step?: number };
  validate: (value: string) => DamEcoSimplifiedValidationKey | null;
  disabled: boolean;
  extra?: { hint?: ReactNode; info?: string; alignRows?: boolean };
}

function stepValue(current: string, delta: number, limits: StepperFieldProps['limits']): string {
  const parsed = parseFormNumber(current);
  const base = parsed != null && Number.isFinite(parsed) ? parsed : (limits?.min ?? 0);
  let next = base + delta;
  if (limits?.min != null) next = Math.max(limits.min, next);
  if (limits?.max != null) next = Math.min(limits.max, next);
  return String(Number(next.toFixed(2)));
}

/** Campo numérico do design: valor editável, unidade e setas (− / + no celular). */
export function StepperField({
  name, label, unit, limits, validate, disabled, extra,
}: Readonly<StepperFieldProps>): ReactElement {
  const { t } = useTranslation();
  const { control } = useFormContext<DamEcoSimplifiedFormValues>();
  const step = limits?.step ?? 1;
  const inputId = `eco-simplificado-${name}`;

  return (
    <div>
      <Label $alignRows={extra?.alignRows}>
        {label}
        {extra?.info ? <InfoHint id={`${inputId}-info`} text={extra.info} /> : null}
      </Label>
      <Controller
        control={control}
        name={name}
        rules={{
          validate: (value) => {
            const errorKey = validate(value);
            return errorKey ? t(errorKey) : true;
          },
        }}
        render={({ field, fieldState }) => (
          <>
            <Box $invalid={!!fieldState.error} $disabled={disabled}>
              <StepButton type="button" $area="dec" aria-label={t('ecoSimplificadoDiminuir', { campo: label, interpolation: { escapeValue: false } })} disabled={disabled} onClick={() => field.onChange(stepValue(field.value, -step, limits))}>
                <ChevronDownIcon className="desktop-icon" size={12} strokeWidth={2.5} />
                <MinusIcon className="mobile-icon" size={18} />
              </StepButton>
              <ValueArea htmlFor={inputId}>
                <ValueInput
                  id={inputId}
                  data-testid={inputId}
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputMode="decimal"
                  style={{ width: `${Math.max(field.value.length, 1) + 0.2}ch` }}
                  disabled={disabled}
                  aria-invalid={!!fieldState.error}
                />
                <Unit>{unit}</Unit>
              </ValueArea>
              <StepButton type="button" $area="inc" aria-label={t('ecoSimplificadoAumentar', { campo: label, interpolation: { escapeValue: false } })} disabled={disabled} onClick={() => field.onChange(stepValue(field.value, step, limits))}>
                <ChevronUpIcon className="desktop-icon" size={12} strokeWidth={2.5} />
                <PlusIcon className="mobile-icon" size={18} />
              </StepButton>
            </Box>
            {fieldState.error ? <ErrorText>{fieldState.error.message}</ErrorText> : null}
          </>
        )}
      />
      {extra?.hint ? <Helper $alignRows={extra.alignRows}>{extra.hint}</Helper> : null}
    </div>
  );
}
